import { useTranslations } from "adapters/i18n";
import retreatService from "features/retiro/retiroService";
import { debounce } from "features/shared/utils";
import { useEffect, useState } from "react";
import farmService from "services/FazendaService";
import {
  UserFilterStatus,
  UserPermission,
  type UserManagementFilter,
} from "../../../userManagement.types";

type UseFilterProps = {
  clientId: number | null;
  filter: UserManagementFilter;
  onFilterChange: (filter: UserManagementFilter) => void;
};

type FilterOption<T extends string | number> = {
  label: string;
  value: T;
};

export function useFilter({
  clientId,
  filter,
  onFilterChange,
}: UseFilterProps) {
  const { t } = useTranslations();

  const [filterState, setFilterState] = useState({
    search: filter.searchValue,
    status: filter.status || UserFilterStatus.All,
    farms: filter.farmIds,
    retreats: filter.retreatIds,
    permissions: filter.permissions,
  });

  const [filterOptions, setFilterOptions] = useState({
    farms: [] as FilterOption<number>[],
    isFarmsLoading: true,
    isRetreatsLoading: true,
    retreats: [] as FilterOption<number>[],
  });

  useEffect(() => {
    const fetch = async () => {
      const farms = await fetchFarms(clientId);
      setFilterOptions((prev) => ({ ...prev, farms, isFarmsLoading: false }));
    };

    fetch();
  }, [clientId]);

  useEffect(() => {
    if (filterOptions.farms.length === 0) {
      return;
    }

    const fetch = async () => {
      const retreats = await fetchRetreats(filterOptions.farms);
      setFilterOptions((prev) => ({
        ...prev,
        retreats,
        isRetreatsLoading: false,
      }));
    };

    fetch();
  }, [filterOptions.farms]);

  useEffect(() => {
    debounce(
      () =>
        onFilterChange({
          farmIds: filterState.farms,
          permissions: filterState.permissions,
          retreatIds: filterState.retreats,
          searchValue: filterState.search,
          status:
            filterState.status === UserFilterStatus.All
              ? null
              : filterState.status,
        }),
      500,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterState.farms,
    filterState.permissions,
    filterState.retreats,
    filterState.search,
    filterState.status,
  ]);

  const onChangeSearch = (value: string) => {
    setFilterState((prev) => ({ ...prev, search: value }));
  };
  const onChangeStatus = (selected: UserFilterStatus) => {
    setFilterState((prev) => ({ ...prev, status: selected }));
  };
  const onChangeFarm = (selected: number[]) => {
    setFilterState((prev) => ({ ...prev, farms: selected }));
  };
  const onChangeRetreat = (selected: number[]) => {
    setFilterState((prev) => ({ ...prev, retreats: selected }));
  };
  const onChangePermission = (selected: UserPermission[]) => {
    setFilterState((prev) => ({ ...prev, permissions: selected }));
  };

  const statusOptions: FilterOption<number>[] = [
    {
      label: t("common:all-male"),
      value: UserFilterStatus.All,
    },
    {
      label: t("common:status.active"),
      value: UserFilterStatus.Active,
    },
    {
      label: t("common:status.inactive"),
      value: UserFilterStatus.Inactive,
    },
  ];

  const permissionsOptions: FilterOption<string>[] = [
    {
      label: t("user:permissions.can-access-views-app"),
      value: UserPermission.CanAccessApp,
    },
    {
      label: t("user:permissions.can-access-portal"),
      value: UserPermission.CanAccessPortal,
    },
    {
      label: t("user:permissions.final-stock"),
      value: UserPermission.FinalStock,
    },
    {
      label: t("user:permissions.import-maps"),
      value: UserPermission.ImportMaps,
    },
    {
      label: t("user:permissions.import-export-pasture-csv"),
      value: UserPermission.ExportAndImportPastureCSV,
    },
    {
      label: t("user:permissions.register-forage-cycle"),
      value: UserPermission.RegisterForageCycle,
    },
    {
      label: t("user:permissions.access-benchmark"),
      value: UserPermission.CanAccessBenchmark,
    },
  ];

  return {
    farms: {
      isLoading: filterOptions.isFarmsLoading,
      onChange: onChangeFarm,
      options: filterOptions.farms,
      selected: filterState.farms,
    },
    permissions: {
      onChange: onChangePermission,
      options: permissionsOptions,
      selected: filterState.permissions,
    },
    retreat: {
      selected: filterState.retreats,
      isLoading: filterOptions.isRetreatsLoading,
      onChange: onChangeRetreat,
      options: filterOptions.retreats,
    },
    search: {
      onChange: onChangeSearch,
      value: filterState.search,
    },
    status: {
      onChange: onChangeStatus,
      options: statusOptions,
      selected: filterState.status,
    },
  };
}

async function fetchFarms(
  clientId: number | null,
): Promise<FilterOption<number>[]> {
  const response = await farmService.listarTodasrFazendasCliente(clientId);
  const farms = (response.data as { IdFazenda: number; Nome: string }[]).map(
    (item) => ({
      value: item.IdFazenda,
      label: item.Nome,
    }),
  );

  return farms;
}

async function fetchRetreats(
  farmOptions: FilterOption<number>[],
): Promise<FilterOption<number>[]> {
  const farmIds = farmOptions.map((item) => item.value);
  const response = await retreatService.listaRetirosPorIdFazenda(farmIds);
  if (!("data" in response)) {
    return [];
  }

  const retreats = (response.data as { IdRetiro: number; Nome: string }[]).map(
    (item) => ({ value: item.IdRetiro, label: item.Nome }),
  );
  return retreats;
}
